import React, { useContext, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { RouteProps, useHistory } from 'react-router-dom';
import Title from '../../components/Title';
import { axiosKK } from '../../services/networkRequest';
import { Order, OrderListResponse, RemovalOrdersResponse, RemovalQuoteRequestsResponse } from '../../models/APIModels';
import Moment from 'moment';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import SubmitButton from '../../components/SubmitButton';
import FileDownload from 'js-file-download';
import { useApi } from '../../services/useApi';
import {
  MapPinIcon,
  TruckIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  TagIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';

function AdminDashboardComponent({
  history,
}: RouteComponentProps) {
  const { user: loggedInUser } = useContext(UserContext);

  const { data: orders = [], loading, error } = useApi<any[]>(
    () =>
      Promise.all([
        axiosKK.get<OrderListResponse>('/orders').catch(() => ({ data: { orders: [] } })),
        axiosKK.get<RemovalOrdersResponse>('/admin/removals/orders').catch(() => ({ data: { orders: [] } })),
        axiosKK.get<RemovalQuoteRequestsResponse>('/admin/removals/quotes').catch(() => ({ data: { quotes: [] } })),
      ]).then(([regularOrdersResult, removalOrdersResult, quoteRequestsResult]) => {
        const quotesWithType = (quoteRequestsResult.data.quotes || []).map((q) => ({
          ...q,
          itemType: 'quote' as const,
        }));
        const allOrders = [
          ...(regularOrdersResult.data.orders || []),
          ...(removalOrdersResult.data.orders || []),
          ...quotesWithType,
        ];
        return allOrders.sort((a, b) => b.createdAt - a.createdAt);
      }),
    [],
    'admin-dashboard-orders'
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {(function () {
        switch (loggedInUser?.role) {
          case Role.Admin:
            return <Title>Admin Dashboard</Title>;
          case Role.Warehouse:
            return <Title>Warehouse Dashboard</Title>;
          default:
            return <Title>Dashboard</Title>;
        }
      })()}

      {loggedInUser && (
        <p className="grey">Welcome back, {loggedInUser?.firstName}</p>
      )}

      <section className="option-box-container">
        {loggedInUser?.role === Role.Admin && (
          <>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/logistics');
              }}
            >
              <div className="img">
                <TruckIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Logistics</h5>
                <p>Orders · Route Plan · Warehouse · Forecast</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/partners');
              }}
            >
              <div className="img">
                <UserGroupIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Partners</h5>
                <p>User Access · Pricing · Vehicles · Service Areas</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/invoices');
              }}
            >
              <div className="img">
                <BanknotesIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Invoices</h5>
                <p>Partner & warehouse monthly invoices</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/products');
              }}
            >
              <div className="img">
                <ArchiveBoxIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Products</h5>
                <p>Create and Edit Products</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/discounts');
              }}
            >
              <div className="img">
                <TagIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Discounts</h5>
                <p>Create and Edit Discounts</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/users');
              }}
            >
              <div className="img">
                <ShieldCheckIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Manage Admins</h5>
                <p>Create and Edit Admin Profiles</p>
              </div>
            </div>
          </>
        )}
        <div
          className="each-box"
          onClick={() => {
            history.push('/admin/customers');
          }}
        >
          <div className="img">
            <MapPinIcon width={24} height={24} />
          </div>
          <div className="content">
            <h5>Customer Profiles</h5>
            <p>Create and Edit Customer Profiles</p>
          </div>
        </div>
      </section>

      <div className="line" />

      <Title>Orders</Title>
      <section className="search-bar">
        <SubmitButton
          onClick={() => {
            axiosKK
              .get(`/orders/CSV`, {
                responseType: 'blob',
              })
              .then((response) => {
                FileDownload(response.data, `OrderBoxes.csv`);
              });
          }}
        >
          Download CSV
        </SubmitButton>
      </section>
      <section className="admin-orders-table-section">
        <div className="admin-orders-table-scroll">
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Created Date</th>
                <th>Customer Name</th>
                <th>Fulfilled</th>
                <th>Order Type</th>
                <th>Collection Date</th>
                <th>Delivery Date</th>
                <th>Partners</th>
              </tr>
            </thead>
            <tbody>
              {loading && orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-orders-table__empty">Loading orders...</td>
                </tr>
              ) : error && orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-orders-table__empty">
                    Failed to load orders. Please refresh the page.
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-orders-table__empty">No orders found</td>
                </tr>
              ) : (
                orders.map((each) => {
                  const itemType = (each as any).itemType || each.orderType || 'order';
                  const uniqueKey = `${itemType}-${each.id}`;
                  return <Row key={uniqueKey} order={each} />;
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

interface RowProps extends RouteProps {
  order: Order;
}

function Row({ order }: RowProps) {
  const history = useHistory();
  const isRemovalOrder = order.orderType === 'removal';
  const isQuoteRequest = (order as any).itemType === 'quote';

  const customerName = (() => {
    if (isQuoteRequest) {
      const q = order as any;
      return q.customer
        ? `${q.customer.firstName || ''} ${q.customer.lastName || ''}`.trim()
        : q.customerEmail || 'Unknown';
    }
    if (order.customer) {
      return `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim();
    }
    return 'Unknown';
  })();

  return (
    <tr
      onClick={() => {
        if (isQuoteRequest) {
          history.push(`/admin/removals/quote/${order.id}`, { quote: order });
        } else {
          history.push(`/admin/order/${order.id}`, { order });
        }
      }}
    >
      <td data-label="ID">{order.id}</td>

      <td data-label="Created At">
        {order.createdAt ? Moment(order.createdAt).format('dddd, MMM Do YYYY') : 'N/A'}
      </td>

      <td data-label="Customer Name">{customerName}</td>

      <td data-label="Fulfilled">
        {isQuoteRequest ? 'N/A' : isRemovalOrder ? 'N/A' : (order.roomPackIncluded ? 'Yes' : 'No')}
      </td>

      <td data-label="Order Type">
        {isQuoteRequest
          ? `Quote - ${(order as any).tierName || 'Unknown'}`
          : isRemovalOrder
          ? 'Removal'
          : order.suitcaseOrder
            ? 'Suitcase'
            : order.storageRequired
              ? 'Storage'
              : 'Shipping'}
      </td>

      <td data-label="Collection Date">
        {isQuoteRequest
          ? 'Pending Quote'
          : isRemovalOrder
          ? (order.moveDate ? Moment(order.moveDate).format('dddd, MMM Do YYYY') : 'Not Set')
          : (order.collectionDate ? Moment(order.collectionDate).format('dddd, MMM Do YYYY') : 'Not Set')}
      </td>

      <td data-label="Delivery Date">
        {isQuoteRequest
          ? ((order as any).status || '').toUpperCase()
          : order.deliveryDate
          ? Moment(order.deliveryDate).format('dddd, MMM Do YYYY')
          : 'Not Set'}
      </td>

      <td data-label="Partners">
        {isQuoteRequest || isRemovalOrder ? (
          ''
        ) : order.storageRequired ? (
          (() => {
            const hasCollection = order.collectionDriverId ?? order.assignedDriverId;
            const hasDelivery = order.deliveryDriverId ?? order.assignedDriverId;
            const hasWarehouse = order.assignedWarehouseId;
            const bothCouriers = hasCollection && hasDelivery;
            if (bothCouriers && hasWarehouse) {
              return <span className="assignment-badge assignment-full">All Assigned</span>;
            }
            if (bothCouriers && !hasWarehouse) {
              return <span className="assignment-badge assignment-couriers">Both Couriers</span>;
            }
            if (hasCollection && hasWarehouse) {
              return <span className="assignment-badge assignment-partial">Collection + Warehouse</span>;
            }
            if (hasDelivery && hasWarehouse) {
              return <span className="assignment-badge assignment-partial">Delivery + Warehouse</span>;
            }
            if (hasCollection) {
              return <span className="assignment-badge assignment-minimal">Collection Courier</span>;
            }
            if (hasDelivery) {
              return <span className="assignment-badge assignment-minimal">Delivery Courier</span>;
            }
            if (hasWarehouse) {
              return <span className="assignment-badge assignment-warehouse">Warehouse Only</span>;
            }
            return <span className="assignment-badge assignment-none">Unassigned</span>;
          })()
        ) : (
          ''
        )}
      </td>
    </tr>
  );
}

export default withRouter(AdminDashboardComponent as any);
